import { useEffect, useRef } from 'react';

import { SparklesIcon } from 'lucide-react';

import { useMarkdownToHtml } from '@/hooks/useMarkdownToHtml';

interface AuthoringValidationBannerProps {
  recommendation: string;
}

export const AuthoringValidationBanner = ({ recommendation }: AuthoringValidationBannerProps) => {
  const ref = useRef<HTMLDivElement>(null);
  const lastScrolledRef = useRef<string>('');
  const { html, error } = useMarkdownToHtml(recommendation);

  // Scroll once per unique recommendation, after the markdown (or fallback <pre>) has
  // committed to the DOM so the scroll container measures the banner's final height.
  // Without the ref guard, later renders from useMarkdownToHtml would re-scroll and yank
  // the user around while they read.
  useEffect(() => {
    if (!recommendation || (!html && !error)) {
      return;
    }
    if (lastScrolledRef.current === recommendation) {
      return;
    }
    lastScrolledRef.current = recommendation;
    ref.current?.scrollIntoView({ block: 'end', behavior: 'smooth' });
  }, [recommendation, html, error]);

  if (!recommendation) {
    return null;
  }
  return (
    <div
      ref={ref}
      role="status"
      aria-live="polite"
      className="flex gap-3 rounded-lg bg-ai-soft p-4"
    >
      <SparklesIcon size={16} className="mt-0.5 shrink-0 text-ai" />
      {error ? (
        <pre className="text-sm whitespace-pre-wrap text-foreground">{recommendation}</pre>
      ) : (
        <div
          className="text-sm leading-5 text-foreground [&>:first-child]:mt-0 [&>:last-child]:mb-0"
          dangerouslySetInnerHTML={{ __html: html }}
        />
      )}
    </div>
  );
};
